import { type ReactNode } from 'react';
import { create } from 'zustand';

type SidePanelKey = 'chat' | 'suggest_controls';

/** Default width (px) of a standard side panel before any user resize. */
export const DEFAULT_PANEL_WIDTH = 400;
/** Default width (px) of a panel opened in wide mode (e.g. chat). */
export const WIDE_PANEL_WIDTH = 500;
/** Smallest width (px) the panel can be dragged to. */
export const MIN_PANEL_WIDTH = 360;
/**
 * Absolute floor for the drag cap (px). A fixed maximum read as a different
 * proportion on every screen — ~62% of a 1440px laptop but only ~35% of a
 * 2560px monitor — so the cap is now primarily viewport-proportional (see
 * {@link MAX_PANEL_WIDTH_FRACTION}); this constant only guarantees a usable
 * width on small screens.
 */
export const MAX_PANEL_WIDTH = 900;
/** Fraction of the viewport the panel may occupy at its widest. */
export const MAX_PANEL_WIDTH_FRACTION = 0.6;

const STORAGE_KEY = 'side-panel-width';
/** Keep at least this much room for the nav + main content when resizing. */
const MIN_CONTENT_SPACE = 200;

/**
 * The widest the panel may be on the current viewport: a fraction of the
 * viewport width (so a larger screen allows a proportionally larger panel),
 * floored by {@link MAX_PANEL_WIDTH} for small screens and always leaving
 * {@link MIN_CONTENT_SPACE} for the nav + main content.
 */
export const getMaxPanelWidth = (): number => {
  if (typeof window === 'undefined') {
    return MAX_PANEL_WIDTH;
  }
  const contentCeiling = window.innerWidth - MIN_CONTENT_SPACE;
  const desired = Math.max(
    MAX_PANEL_WIDTH,
    window.innerWidth * MAX_PANEL_WIDTH_FRACTION
  );

  return Math.max(MIN_PANEL_WIDTH, Math.min(desired, contentCeiling));
};

/** Clamp a requested width to the allowed range and the current viewport. */
export const clampPanelWidth = (width: number): number =>
  Math.min(Math.max(width, MIN_PANEL_WIDTH), getMaxPanelWidth());

const readStoredWidth = (): number | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }
  const parsed = Number(raw);

  return Number.isFinite(parsed) ? clampPanelWidth(parsed) : null;
};

/**
 * Resolve the width to render: a user-resized width takes precedence,
 * otherwise fall back to the standard/wide default for the panel.
 */
export const getEffectivePanelWidth = (
  panelWidth: number | null,
  isWidePanel: boolean
): number =>
  panelWidth ?? (isWidePanel ? WIDE_PANEL_WIDTH : DEFAULT_PANEL_WIDTH);

export interface SidePanelState {
  isOpen: boolean;
  isWidePanel: boolean;
  /** User-resized width in px, or null to use the standard/wide default. */
  panelWidth: number | null;
  /** True while the user is actively dragging the resize handle. */
  isResizing: boolean;
  /** True when the panel is expanded to a full-screen overlay. */
  isFullScreen: boolean;
  //no-dd-sa
  closeOnLocationChange: boolean;
  close: () => void;
  locationChanged: () => void;
  content: ReactNode | undefined | null;
  key: SidePanelKey | undefined;
  open: (
    key: SidePanelKey,
    content: ReactNode,
    closeOnLocationChange: boolean,
    isWidePanelRequired: boolean
  ) => void;
  setPanelWidth: (width: number) => void;
  setResizing: (resizing: boolean) => void;
  toggleFullScreen: () => void;
}

export const useSidePanelStore = create<SidePanelState>((set) => ({
  isOpen: false,
  isWidePanel: false,
  panelWidth: readStoredWidth(),
  isResizing: false,
  isFullScreen: false,
  closeOnLocationChange: true,
  content: undefined,
  key: undefined,
  close: () => {
    set(() => ({
      isOpen: false,
      content: undefined,
      isFullScreen: false,
    }));
  },
  locationChanged: () => {
    set((state) => {
      let content = state.content;

      if (state.closeOnLocationChange) {
        content = undefined;
      }

      const isOpen = state.isOpen && !state.closeOnLocationChange;

      return {
        isOpen,
        content,
        // Drop full-screen if the navigation closed the panel.
        isFullScreen: isOpen ? state.isFullScreen : false,
      };
    });
  },
  open: (
    key: SidePanelKey,
    content: ReactNode,
    shouldCloseOnLocationChange: boolean,
    isWidePanelRequired: boolean
  ) => {
    set(() => ({
      key,
      content,
      isOpen: true,
      isWidePanel: isWidePanelRequired,
      closeOnLocationChange: shouldCloseOnLocationChange,
      isFullScreen: false,
    }));
  },
  setPanelWidth: (width: number) => {
    const clamped = clampPanelWidth(width);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, String(clamped));
    }
    set(() => ({ panelWidth: clamped }));
  },
  setResizing: (resizing: boolean) => {
    set(() => ({ isResizing: resizing }));
  },
  toggleFullScreen: () => {
    set((state) => ({ isFullScreen: !state.isFullScreen }));
  },
}));
